import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
// Importing Legacy System Tax Icons
import icon1 from '../assets/LegacySystemTax-icon-1.png';
import icon2 from '../assets/LegacySystemTax-icon-2.png';
import icon3 from '../assets/LegacySystemTax-icon-3.png';
import icon4 from '../assets/LegacySystemTax-icon-4.png';
import icon5 from '../assets/LegacySystemTax-icon-5.png';
import icon6 from '../assets/LegacySystemTax-icon-6.png';
import { ReactComponent as TMSVector } from '../assets/LegacySystemTax-vector.svg';
import TmsLogo from '../assets/tms-logo-black.png';

const LegacySystemTax = () => {
    const videoRef = useRef(null);
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    // Lazy load video when it comes into viewport
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !isVideoLoaded) {
                        setIsVideoLoaded(true);
                    }
                });
            },
            {
                rootMargin: '200px', // Start loading 200px before video is visible
            }
        );

        if (videoRef.current) {
            observer.observe(videoRef.current);
        }

        return () => {
            if (videoRef.current) {
                observer.unobserve(videoRef.current);
            }
        };
    }, [isVideoLoaded]);

    const cards = [
        {
            id: 1,
            title: "Broken Inefficient Trading Workflow",
            description: "Managing positions and interests across multiple trading protocols across multiple ECNs, to suit your trading needs are complex and difficult to achieve, leaving money on the table.",
            isDark: true,
            iconName: icon1
        },
        {
            id: 2,
            title: "Fragmented Trade Lifecycle Visibility",
            description: "Trade execution, clearing, settlement, and regulatory reporting live in separate systems, making it hard to answer a simple question: Is this trade fully complete, cleared, and reported correctly on time?",
            isDark: true,
            iconName: icon2
        },
        {
            id: 3,
            title: "Late Discovery of Breaks & Exceptions",
            description: "Breaks are often found end-of-day or next-day, turning manageable issues into urgent escalations.",
            isDark: true,
            iconName: icon3
        },
        {
            id: 4,
            title: "Fixed Income Trading - Elephant In The Room",
            description: "Traders, managers, management, operations, risk and compliance all seem to have different views and different systems, making it difficult to have a unified view of the business across all groups.",
            isDark: true,
            iconName: icon4
        },
        {
            id: 5,
            title: "Manual, Spreadsheet-Heavy P&L & Payouts, Finance Charges",
            description: "Daily P&L and monthly payouts are often assembled manually, making them slow, fragile, and dependent on a small number of spreadsheet experts. Custodian funding charges arrive at a firm level and are difficult to allocate cleanly, leading to confusion and payout disputes.",
            isDark: true,
            iconName: icon5
        },
        {
            id: 6,
            title: "Limited Management Insight",
            description: "Leadership lacks modern business intelligence tools for a real-time, unified view of P&L, inventory, aging, risk, and performance across desks.",
            isDark: true,
            iconName: icon6
        }
    ];

    return (
        <div>
            <section className="w-full bg-[#070E21] py-20 px-4 sm:px-6 lg:px-8 text-black">
                <div className="max-w-[1271px] mx-auto">
                    {/* Header */}

                    <div className="text-center mb-16">
                        <h2 className="font-oswald text-white text-4xl md:text-5xl font-bold mb-4 uppercase">
                            REAL PROBLEM TODAY: <span className="text-red-600">"THE LEGACY TAX"</span>
                        </h2>
                        <p className="font-inter text-gray-200 text-lg md:text-2xl">
                            You're paying top-tier pricing, but still relying on spreadsheets and people to glue everything together."
                        </p>
                    </div>

                    {/* Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[26px]">
                        {cards.map((card) => (
                            <div
                                key={card.id}
                                className={`
                                rounded-md p-8 flex flex-col justify-start h-full min-h-[320px]
                                ${card.isDark
                                        ? 'bg-[#1B202F] text-white'
                                        : 'bg-white border-transparent text-[#070E21]'
                                    }
                            `}
                            >
                                {/* Icon */}
                                <div className="w-12 h-12 mb-6">
                                    <img
                                        src={card.iconName}
                                        alt=""
                                        className="w-full h-full object-contain"
                                        loading="lazy"
                                    />
                                </div>

                                <h3 className="font-inter text-xl font-bold mb-4 leading-tight">
                                    {card.title}
                                </h3>

                                <p className={`font-inter text-[15px] leading-relaxed ${card.isDark ? 'text-gray-200' : 'text-gray-600'}`}>
                                    {card.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            <section className="w-full bg-white py-20 px-4 sm:px-6 lg:px-8 text-black">
                <div className="max-w-[1271px] mx-auto">
                    <div className="text-center mx-auto mt-16">
                        <div className="flex items-center justify-center gap-3 mb-12">
                            <TMSVector className="relative top-[-40px] sm:top-[-50px] md:top-[-60px] lg:top-[-30px] xl:top-[-30px] w-12  sm:w-10 md:w-14 md:h-14" />
                            <h2 className="font-oswald text-3xl md:text-5xl font-bold uppercase tracking-wide">
                                THE PRACTICAL SOLUTION IS - <span className="inline-flex items-center gap-2"><img src={TmsLogo} alt="TMS Logo" className="w-24 h-auto relative top-[2px]  md:w-36  inline-block" /></span>
                            </h2>
                        </div>
                        <p className="font-inter font-bold text-black text-xl md:px-20 md:text-2xl mb-6 leading-relaxed">
                            FGC TMS, trusted by broker dealers worldwide, is designed for trading and operational excellence—built to solve real problems, now.
                        </p>
                        <p className="font-inter font-bold text-black text-lg md:text-2xl leading-relaxed">
                            View the video below to know how FGC TMS eliminates the legacy tax.
                        </p>
                        {/* Down Arrow Icon */}
                        <div className="flex justify-center mt-8">
                            <div className="w-16 h-16 rounded-full bg-[#00B4D8] flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Video Section */}
                    <div ref={videoRef} className="flex items-center justify-center min-h-[250px] md:min-h-[600px] w-full mt-12">
                        <div className="relative w-full max-w-[991px] aspect-video bg-gray-900 rounded-lg overflow-hidden shadow-xl">
                            {isVideoLoaded ? (
                                <video
                                    controls
                                    preload="none"
                                    playsInline
                                    className="w-full h-full"
                                    poster="/videos/LegacySystemTax-poster.jpg"
                                >
                                    {/* FOR DEVELOPMENT: Use local path */}
                                    <source src="/videos/LegacySystemTax-video.mp4" type="video/mp4" />

                                    {/* FOR PRODUCTION: Replace with S3 URL */}
                                    {/* <source src="https://your-bucket.s3.region.amazonaws.com/videos/LegacySystemTax-video.mp4" type="video/mp4" /> */}

                                    Your browser does not support the video tag.
                                </video>
                            ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                    <div className="text-center">
                                        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white mb-4"></div>
                                        <p className="text-gray-400">Loading video...</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="text-center mx-auto">
                        <Link
                            to="/schedule-demo"
                            className="bg-[#00C2FF] hover:bg-[#00a0d6] text-white font-bold rounded-[16px] border border-transparent transition-colors shadow-sm inline-block text-center"
                            style={{
                                padding: '10px 40px',
                                fontSize: '18px', // Approximate size for button text logic
                            }}
                        >
                            Request a demo
                        </Link>
                    </div>
                </div>
            </section >
        </div>
    );
};

export default LegacySystemTax;
